import { useLazyQuery, useMutation } from '@apollo/client';
import { Form, Modal, Switch, Tooltip } from 'antd';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as ArchiveIcon } from '../../../../../assets/svg/box-archive-solid.svg';
import { ReactComponent as EditIcon } from '../../../../../assets/svg/pen-to-square-solid.svg';
import { ReactComponent as UnarchiveIcon } from '../../../../../assets/svg/right-from-bracket-solid.svg';
import CommonTable from '../../../../../components/CommonTable';
import SearchComponent from '../../../../../components/SearchComponent';
import {
  BREAKPOINTS,
  DATE_FORMATS,
  DYNAMIC_CONFIG_TYPES,
  LIMIT,
  PERMISSIONS_KEY,
  PERMISSION_TYPE,
  SORT,
  TAGS_CONFIG_SUBTYPES,
} from '../../../../../common/constants';
import {
  formatDate,
  hasPermission,
  openNotification,
} from '../../../../../common/utils';
import { DYNAMIC_CONFIG_ITEMS } from '../../graphql/Queries';
import { CREATE_DYNAMIC_CONFIG_ITEM, UPDATE_DYNAMIC_CONFIG_ITEM } from '../../graphql/Mutations';
import ConfigTagModal from './ConfigTagModal';

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialSort = {
  field: 'createdAt',
  order: 'DESC',
};

const ConfigTagsTable = ({
  configSubtype,
  addTagModalScopeLabel,
  isTagModalOpen,
  setIsTagModalOpen,
  currentTag,
  setCurrentTag,
}) => {
  const {
    state: { permissions },
  } = useContext(AppContext);

  const [form] = Form.useForm();

  // review tags hold a plain value instead of an emoji icon
  const isReviewTab =
    configSubtype === TAGS_CONFIG_SUBTYPES?.REVIEW?.value;

  const [tagsList, setTagsList] = useState([]);
  const [tagsSearchTerm, setTagsSearchTerm] = useState('');
  const [showOnlyArchived, setShowOnlyArchived] = useState(false);
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [isTagsLoading, setIsTagsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const [isArchivePrompt, setIsArchivePrompt] = useState(false);
  const [tagToArchive, setTagToArchive] = useState(null);

  const [createTagsConfig] = useMutation(CREATE_DYNAMIC_CONFIG_ITEM, {
    onError: () => {},
  });

  const [updateTagsConfig] = useMutation(UPDATE_DYNAMIC_CONFIG_ITEM, {
    onError: () => {},
  });

  const hasUpdatePermission = hasPermission(
    PERMISSIONS_KEY?.MASTER_LIST_TAG,
    PERMISSION_TYPE?.UPDATE,
    permissions,
  );

  const hasArchivePermission = hasPermission(
    PERMISSIONS_KEY?.MASTER_LIST_TAG,
    PERMISSION_TYPE?.DELETE,
    permissions,
  );

  const [fetchDynamicConfigItems] = useLazyQuery(DYNAMIC_CONFIG_ITEMS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const rows = data?.dynamicConfigItems?.data;
      setTagsList(Array.isArray(rows) ? rows : []);
      const total = data?.dynamicConfigItems?.count ?? 0;
      setPaginationProp((prev) => ({ ...prev, total, current: prev?.current }));
      setIsTagsLoading(false);
    },
    onError: () => {
      setIsTagsLoading(false);
      setTagsList([]);
    },
  });

  const buildSort = (sorter) =>
    sorter?.column
      ? {
          field: sorter?.field,
          order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
        }
      : initialSort;

  const fetchTagsConfigs = ({
    skip = (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
    limit = paginationProp?.pageSize || LIMIT,
    search = tagsSearchTerm,
    sorter = sortedInfo,
    archivedOnly = showOnlyArchived,
  } = {}) => {
    setIsTagsLoading(true);

    const trimSearch = search?.trim();

    fetchDynamicConfigItems({
      variables: {
        filter: {
          configType: DYNAMIC_CONFIG_TYPES?.TAGS?.value,
          configSubtypes: configSubtype ? [configSubtype] : [],
          skip,
          limit,
          search: trimSearch || null,
          // Active list by default; archived (inactive) when "Only Archived" is on
          isActive: !archivedOnly,
        },
        sort: buildSort(sorter),
      },
    });
  };

  useEffect(() => {
    setPaginationProp((prev) => ({ ...prev, current: 1 }));
    fetchTagsConfigs({
      skip: 0,
      limit: paginationProp?.pageSize || LIMIT,
      archivedOnly: showOnlyArchived,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configSubtype, showOnlyArchived]);

  const handleTableChange = (pagination, _tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);

    setPaginationProp((prev) => ({
      ...prev,
      ...pagination,
      current,
      pageSize: pagination?.pageSize || prev?.pageSize,
    }));
    setSortedInfo(sorter);

    fetchTagsConfigs({
      skip,
      limit: pagination?.pageSize || LIMIT,
      sorter,
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setTagsSearchTerm(trimValue);
    setPaginationProp((prev) => ({ ...prev, current: 1, skip: 0 }));
    fetchTagsConfigs({ skip: 0, search: trimValue });
  };

  const handleSubmitTag = async (values) => {
    try {
      setIsActionLoading(true);

      const payloadBase = {
        description: values?.description?.trim(),
        itemKey: values?.name,
        value: values?.icon,
      };

      if (currentTag?.id) {
        const response = await updateTagsConfig({
          variables: {
            where: {
              id: currentTag?.id,
              configSubtype: currentTag?.configSubtype,
              configType: currentTag?.configType,
            },
            data: {
              ...payloadBase,
              isActive: currentTag?.isActive,
            },
          },
        });
        if (!response?.data?.updateDynamicConfigItem?.data?.id) {
          throw new Error(
            response?.data?.updateDynamicConfigItem?.message ||
              'Unable to update tag. Please try again.',
          );
        }
      } else {
        const response = await createTagsConfig({
          variables: {
            data: {
              ...payloadBase,
              configType: DYNAMIC_CONFIG_TYPES?.TAGS?.value,
              configSubtype,
              isActive: true,
            },
          },
        });
        if (!response?.data?.createDynamicConfigItem?.data?.itemKey) {
          throw new Error(
            response?.data?.createDynamicConfigItem?.message ||
              'Unable to create tag. Please try again.',
          );
        }
      }

      setIsTagModalOpen(false);
      setCurrentTag(null);
      setIsActionLoading(false);
      setPaginationProp((prev) => ({ ...prev, current: 1 }));
      fetchTagsConfigs({
        skip: 0,
        limit: paginationProp?.pageSize || LIMIT,
      });
    } catch (e) {
      setIsActionLoading(false);
    }
  };

  const handleArchiveTag = async () => {
    if (!tagToArchive?.id) return;
    try {
      setIsActionLoading(true);
      const shouldArchive = !!tagToArchive?.isActive;

      await updateTagsConfig({
        variables: {
          where: {
            id: tagToArchive?.id,
            configSubtype: tagToArchive?.configSubtype,
            configType: tagToArchive?.configType,
          },
          data: { isActive: !shouldArchive },
        },
      });
      setIsArchivePrompt(false);
      setTagToArchive(null);
      setIsActionLoading(false);
      setPaginationProp((prev) => ({ ...prev, current: 1 }));
      fetchTagsConfigs({
        skip: 0,
        limit: paginationProp?.pageSize || LIMIT,
      });
    } catch (e) {
      setIsActionLoading(false);
      openNotification('error', 'Something went wrong while updating tag status');
    }
  };

  const columns = useMemo(
    () => [
      {
        title: isReviewTab ? 'KEY' : 'NAME',
        dataIndex: 'itemKey',
        key: 'itemKey',
        ellipsis: true,
        width: 220,
        align: 'left',
        sorter: true,
        render: (_, record) => record?.itemKey || '-',
      },
      {
        title: isReviewTab ? 'VALUE' : 'ICON',
        dataIndex: 'value',
        key: 'value',
        ellipsis: true,
        width: 180,
        align: 'left',
        render: (_, record) => {
          const val = record?.value;
          const isUrl = typeof val === 'string' && val?.startsWith('http');
          return isUrl ? (
            <img
              src={val}
              alt={record?.itemKey}
              style={{ width: 28, height: 28, objectFit: 'contain' }}
            />
          ) : (
            <span>{val || '-'}</span>
          );
        },
      },
      {
        title: 'DESCRIPTION',
        dataIndex: 'description',
        key: 'description',
        ellipsis: true,
        className: 'table-tooltip-cell',
        render: (_, record) => (
          <Tooltip
            title={record?.description ? record?.description : 'No Description'}
            placement="topLeft"
            trigger="click"
            className="pointer"
          >
            {record?.description || '-'}
          </Tooltip>
        ),
      },
      {
        title: 'CREATED AT',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 160,
        align: 'left',
        ellipsis: true,
        sorter: true,
        render: (_, record) =>
          formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
      },
      {
        title: 'ACTIONS',
        dataIndex: 'actions',
        key: 'actions',
        width: 180,
        align: 'left',
        // eslint-disable-next-line no-undef
        fixed: window.innerWidth > BREAKPOINTS.desktop ? 'right' : false,
        render: (_, record) => (
          <div className="action-button">
            {!record?.isActive ? (
              hasArchivePermission && (
                <Tooltip title="Unarchive" placement="top" zIndex={4}>
                  <UnarchiveIcon
                    className="mr-16 pointer svg-icon"
                    onClick={() => {
                      setTagToArchive(record);
                      setIsArchivePrompt(true);
                    }}
                  />
                </Tooltip>
              )
            ) : (
              <>
                {hasUpdatePermission && (
                  <Tooltip title="Edit" placement="top" zIndex={4}>
                    <EditIcon
                      className="mr-16 pointer svg-icon"
                      onClick={() => {
                        setCurrentTag(record);
                        setIsTagModalOpen(true);
                      }}
                    />
                  </Tooltip>
                )}
                {hasArchivePermission && (
                  <Tooltip title="Archive" placement="top" zIndex={4}>
                    <ArchiveIcon
                      className="mr-16 pointer svg-icon"
                      onClick={() => {
                        setTagToArchive(record);
                        setIsArchivePrompt(true);
                      }}
                    />
                  </Tooltip>
                )}
              </>
            )}
          </div>
        ),
      },
    ],
    [
      hasArchivePermission,
      hasUpdatePermission,
      isReviewTab,
      setCurrentTag,
      setIsTagModalOpen,
    ],
  );

  const locale = {
    emptyText: isTagsLoading ? <span /> : 'No data found',
  };

  return (
    <>
      <ConfigTagModal
        form={form}
        tag={currentTag}
        addScopeLabel={addTagModalScopeLabel}
        isEmojiValue={!isReviewTab}
        nameFieldLabel={isReviewTab ? 'Key' : 'Name'}
        loadings={isActionLoading}
        isModalOpen={isTagModalOpen}
        setIsModalOpen={setIsTagModalOpen}
        onClose={() => setCurrentTag(null)}
        onFinish={handleSubmitTag}
      />

      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isArchivePrompt}
        onOk={handleArchiveTag}
        onCancel={() => {
          setIsArchivePrompt(false);
          setTagToArchive(null);
        }}
        okButtonProps={{ loading: isActionLoading }}
      >
        Are you sure you want to {tagToArchive?.isActive ? 'archive' : 'unarchive'}{' '}
        <strong>{tagToArchive?.itemKey || 'this tag'}</strong>?
      </Modal>

      <div className="d-flex justify-end mb-16 flex-wrap">
        <div className="mr-16 d-flex align-center">
          <h4 className="m-0 mr-8">Only Archived</h4>
          <Switch
            size="small"
            checked={showOnlyArchived}
            onChange={(checked) => setShowOnlyArchived(checked)}
          />
        </div>
        <SearchComponent
          className="search-box"
          query={tagsSearchTerm}
          setQuery={setTagsSearchTerm}
          getData={handleSearch}
          name="Tag"
        />
      </div>

      <CommonTable
        locale={locale}
        columns={columns}
        data={tagsList || []}
        loadingData={isTagsLoading}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(record) => record?.id}
      />
    </>
  );
};

export default ConfigTagsTable;
